import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Email, EmailActionContext } from '@/types/email';
import type { ReplyContext } from '@/types/ui';
import type { SenderDigestState } from '@/stores/senderGroupingStore';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { getBrandfetchLogoUrl } from '@/lib/avatar';
import { cn } from '@/lib/utils';
import { sanitizeEmailHtml, type SanitizedEmail } from '@/lib/emailHtmlSanitizer';
import { MessageBody } from '@/features/email-detail/components/MessageBody';
import { ActionButtons } from '@/features/email-detail/components/ActionButtons';
import { MobileActionBar } from '@/features/email-detail/components/MobileActionBar';
import { type ReplyMode } from '@/features/email-detail/components/MessageMetadata';
import { useTrainMessage } from '@/hooks/useTrainMessage';
import { useAccountStore } from '@/stores/accountStore';
import { useMoveToFolderStore } from '@/stores/moveToFolderStore';
import { buildReplyContextWithFallback } from '@/features/email-detail/utils/replyContext';

const HYDRATION_WINDOW = 80;

interface SenderDigestViewProps {
  digest: SenderDigestState;
  onClose: () => void;
  onMessageVisible?: (email: Email) => void;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  onArchive: (emailId: string, context?: EmailActionContext) => void;
  onDelete: (emailId: string, context?: EmailActionContext) => void;
  onReply?: (context: ReplyContext) => void;
}

interface EmailBodyCacheEntry {
  signature: string;
  html: SanitizedEmail | null;
}

export function SenderDigestView({
  digest,
  onClose,
  onMessageVisible,
  onLoadMore,
  isLoadingMore = false,
  onArchive,
  onDelete,
  onReply,
}: SenderDigestViewProps) {
  const { group } = digest;
  const accounts = useAccountStore((state) => state.accounts);

  // Set of user's own email addresses (lowercase) for filtering CC on Reply All
  const accountEmails = useMemo(() => {
    const emails = new Set<string>();
    accounts.forEach((account) => {
      if (account.emailAddress) {
        emails.add(account.emailAddress.toLowerCase());
      }
    });
    return emails;
  }, [accounts]);

  const sortedEmails = useMemo(
    () =>
      [...digest.emails].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      ),
    [digest.emails],
  );
  const digestHasMore = !digest.fullyLoaded && digest.emails.length < digest.totalMessages;
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const loadMoreObserverRef = useRef<IntersectionObserver | null>(null);
  const canLoadMore = typeof onLoadMore === 'function' && digestHasMore;
  const pendingLoadRef = useRef<boolean>(false);

  const emailBodyCacheRef = useRef<Map<string, EmailBodyCacheEntry>>(new Map());
  const digestSignature = useMemo(
    () => sortedEmails.map((email) => email.id).join('|'),
    [sortedEmails],
  );
  const [hydratedIds, setHydratedIds] = useState<Set<string>>(
    () => new Set(sortedEmails.map((email) => email.id)),
  );
  const [activeEmailId, setActiveEmailId] = useState<string | null>(() => sortedEmails[0]?.id ?? null);
  const activeVisibilityRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const cacheRef = emailBodyCacheRef.current;
    const observerRef = loadMoreObserverRef.current;
    return () => {
      cacheRef.clear();
      observerRef?.disconnect();
      loadMoreObserverRef.current = null;
    };
  }, []);

  useEffect(() => {
    emailBodyCacheRef.current.clear();
    setHydratedIds(new Set(sortedEmails.map((email) => email.id)));
  }, [group.key, digestSignature, sortedEmails]);

  useEffect(() => {
    setActiveEmailId((prev) => {
      if (prev && sortedEmails.some((email) => email.id === prev)) {
        return prev;
      }
      return sortedEmails[0]?.id ?? null;
    });
    activeVisibilityRef.current.clear();
  }, [sortedEmails]);
  useEffect(() => {
    pendingLoadRef.current = false;
  }, [group.key]);

  const emailIndexMap = useMemo(() => {
    const indexMap = new Map<string, number>();
    sortedEmails.forEach((email, index) => indexMap.set(email.id, index));
    return indexMap;
  }, [sortedEmails]);

  const activeEmail = useMemo(() => {
    if (!activeEmailId) {
      return sortedEmails[0] ?? null;
    }
    return sortedEmails.find((email) => email.id === activeEmailId) ?? sortedEmails[0] ?? null;
  }, [activeEmailId, sortedEmails]);

  const {
    trainAsSpam,
    trainAsGood,
    isTrainingSpam,
    isTrainingGood,
    canTrainSpam,
    canTrainGood,
  } = useTrainMessage(activeEmail, { source: 'mobile_action_bar' });

  const scheduleHydration = useCallback(
    (email: Email) => {
      const emailId = email.id;
      setHydratedIds((prev) => {
        if (prev.has(emailId)) {
          return prev;
        }
        const next = new Set(prev);
        const index = emailIndexMap.get(emailId) ?? 0;
        const halfWindow = Math.max(1, Math.floor(HYDRATION_WINDOW / 2));
        const start = Math.max(0, index - halfWindow);
        const end = Math.min(sortedEmails.length, index + halfWindow);
        for (let i = start; i < end; i += 1) {
          next.add(sortedEmails[i].id);
        }
        return next;
      });
    },
    [emailIndexMap, sortedEmails],
  );

  const handleCardActiveChange = useCallback(
    (email: Email, info: { isVisible: boolean; ratio: number }) => {
      if (info.isVisible) {
        activeVisibilityRef.current.set(email.id, info.ratio);
      } else {
        activeVisibilityRef.current.delete(email.id);
      }
      let nextActiveId: string | null = null;
      let bestRatio = 0;
      activeVisibilityRef.current.forEach((ratio, id) => {
        if (ratio > bestRatio + 0.01) {
          bestRatio = ratio;
          nextActiveId = id;
          return;
        }
        if (!nextActiveId && ratio === bestRatio) {
          nextActiveId = id;
          bestRatio = ratio;
        }
      });
      if (!nextActiveId && sortedEmails.length) {
        nextActiveId = sortedEmails[0].id;
      }
      setActiveEmailId((prev) => (prev === nextActiveId ? prev : nextActiveId));
    },
    [sortedEmails],
  );

  // Memoize HTML for all emails to avoid rebuilding on every render
  // Only include emails that have full HTML content
  const emailBodyHtmlMap = useMemo(() => {
    const cache = emailBodyCacheRef.current;
    const activeIds = new Set<string>();
    const map = new Map<string, SanitizedEmail>();

    sortedEmails.forEach((email) => {
      if (!hydratedIds.has(email.id)) {
        return;
      }
      activeIds.add(email.id);
      const signature = getEmailBodySignature(email);
      const cached = cache.get(email.id);
      if (cached && cached.signature === signature) {
        if (cached.html !== null) {
          map.set(email.id, cached.html);
        }
        return;
      }

      const html = buildEmailBodyHtml(email);
      const nextEntry: EmailBodyCacheEntry = {
        signature,
        html,
      };
      cache.set(email.id, nextEntry);
      if (html !== null) {
        map.set(email.id, html);
      }
    });

    cache.forEach((_, key) => {
      if (!activeIds.has(key)) {
        cache.delete(key);
      }
    });

    return map;
  }, [hydratedIds, sortedEmails]);


  const handleReplyForEmail = useCallback(
    async (email: Email, modeType: ReplyMode) => {
      if (!onReply) {
        return;
      }
      const sanitizedHtml =
        emailBodyHtmlMap.get(email.id)?.body ??
        email.bodyHtml ??
        email.body ??
        null;
      const context = await buildReplyContextWithFallback(email, modeType, { sanitizedHtml, accountEmails });
      if (!context) {
        return;
      }
      onReply(context);
    },
    [emailBodyHtmlMap, onReply, accountEmails],
  );

  const handleActiveReplyAction = useCallback(
    async (modeType: ReplyMode) => {
      if (!activeEmail) {
        return;
      }
      await handleReplyForEmail(activeEmail, modeType);
    },
    [activeEmail, handleReplyForEmail],
  );


  useEffect(() => {
    if (!canLoadMore || isLoadingMore) {
      return;
    }
    loadMoreObserverRef.current?.disconnect();
    loadMoreObserverRef.current = null;
    const viewport = viewportRef.current;
    const sentinel = loadMoreRef.current;
    if (!viewport || !sentinel) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || pendingLoadRef.current) {
          return;
        }
        pendingLoadRef.current = true;
        onLoadMore?.();
      },
      { root: viewport, threshold: 0, rootMargin: '400px 0px 400px 0px' },
    );
    observer.observe(sentinel);
    loadMoreObserverRef.current = observer;
    return () => {
      observer.disconnect();
      if (loadMoreObserverRef.current === observer) {
        loadMoreObserverRef.current = null;
      }
    };
  }, [canLoadMore, isLoadingMore, onLoadMore]);

  useEffect(() => {
    if (!isLoadingMore) {
      pendingLoadRef.current = false;
    }
  }, [isLoadingMore]);


  const primaryEmail =
    group.senderEmail ||
    digest.emails[0]?.from.email ||
    group.emails[0]?.from.email ||
    '';
  const displayName =
    group.senderName ||
    digest.emails[0]?.from.name ||
    group.emails[0]?.from.name ||
    primaryEmail;
  const avatarSrc = primaryEmail ? getBrandfetchLogoUrl(primaryEmail) : null;
  const unreadLabel =
    typeof digest.unreadCount === 'number' && digest.unreadCount > 0
      ? `${digest.unreadCount} unread`
      : null;
  const messageTotal =
    typeof digest.totalMessages === 'number' && Number.isFinite(digest.totalMessages)
      ? digest.totalMessages
      : sortedEmails.length;

  return (
    <div className="flex h-full flex-col glass-page animate-fade-in-up">
      <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
          <Avatar className="h-9 w-9 flex-shrink-0 ring-1 ring-border">
            {avatarSrc && (
              <AvatarImage src={avatarSrc} alt={displayName || primaryEmail || 'Sender avatar'} />
            )}
            <AvatarFallback className="text-xs">
              {getInitials(displayName, primaryEmail)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{displayName || primaryEmail || 'Unknown sender'}</div>
            <div className="text-xs text-muted-foreground">
              {messageTotal}{' '}
              {messageTotal === 1 ? 'message in this mailbox' : 'messages in this mailbox'}
            </div>
          </div>
        </div>
        {unreadLabel && (
          <Badge variant="secondary" className="text-xs px-2 py-0">
            {unreadLabel}
          </Badge>
        )}
      </div>

      <ScrollArea className="flex-1" viewportRef={viewportRef}>
        {/* pb clears the floating mobile action bar until md, where it hides */}
        <div className="space-y-3 px-4 pt-4 pb-28 md:pb-6">
          {sortedEmails.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border/60 bg-background/80 p-6 text-center text-sm text-muted-foreground">
              No messages from this sender in the current mailbox.
            </div>
          ) : (
            <>
              {sortedEmails
                .map((email) => {
                const isHydrated = hydratedIds.has(email.id);
                const bodyHtml = isHydrated ? emailBodyHtmlMap.get(email.id) : undefined;

                return (
                  <DigestMessageCard
                    key={email.id}
                    email={email}
                    onVisible={onMessageVisible}
                    onHydrate={scheduleHydration}
                    onActiveChange={handleCardActiveChange}
                    className={cn(
                      'rounded-2xl border border-border/60 bg-background/80 p-4 shadow-sm',
                      !email.read && 'border-primary/50',
                    )}
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                          <span
                            className={cn(
                              'text-sm font-semibold text-foreground',
                              !email.read && 'font-bold',
                            )}
                          >
                            {email.subject || 'No subject'}
                          </span>
                          {!email.read && (
                            <Badge variant="default" className="text-[10px] uppercase px-2 py-0">
                              Unread
                            </Badge>
                          )}
                        </div>
                        {/* Desktop: standard per-message action row (mobile
                            routes through the floating action bar below). */}
                        <ActionButtons
                          className="-my-1.5 flex-shrink-0"
                          emailId={email.id}
                          onArchive={(targetId) =>
                            onArchive(targetId, {
                              accountId: email.accountId ?? null,
                              folderId: email.folderId ?? null,
                            })
                          }
                          onDelete={(targetId) =>
                            onDelete(targetId, {
                              accountId: email.accountId ?? null,
                              folderId: email.folderId ?? null,
                            })
                          }
                          onReply={onReply ? (mode) => void handleReplyForEmail(email, mode) : undefined}
                          onMoveToFolder={() => useMoveToFolderStore.getState().open([email.id])}
                        />
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDigestDate(email.date)}
                      </div>
                    </div>
                    {isHydrated && bodyHtml ? (
                      <div className="mt-3">
                        <div className="max-w-none rounded-xl border border-border/60 bg-background/90 p-4 text-sm leading-relaxed shadow-inner">
                          <MessageBody
                            sanitizedHtml={bodyHtml}
                            minHeight="min-h-0"
                            contentClassName="!max-w-none"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 rounded-xl border border-dashed border-border/60 bg-background/50 p-4 text-xs text-muted-foreground animate-pulse">
                        Loading preview…
                      </div>
                    )}
                  </DigestMessageCard>
                );
            })
            .filter(Boolean)}
              {canLoadMore && <div ref={loadMoreRef} className="h-6 w-full" />}
              {isLoadingMore && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>
      {activeEmail && (
        <MobileActionBar
          emailId={activeEmail.id}
          isRead={activeEmail.read}
          onArchive={(targetId) =>
            onArchive(targetId, {
              accountId: activeEmail.accountId ?? null,
              folderId: activeEmail.folderId ?? null,
            })
          }
          onDelete={(targetId) =>
            onDelete(targetId, {
              accountId: activeEmail.accountId ?? null,
              folderId: activeEmail.folderId ?? null,
            })
          }
          onReply={handleActiveReplyAction}
          canTrainSpam={canTrainSpam}
          canTrainGood={canTrainGood}
          isTrainingSpam={isTrainingSpam}
          isTrainingGood={isTrainingGood}
          onTrainSpam={trainAsSpam}
          onTrainGood={trainAsGood}
        />
      )}
    </div>
  );
}

function getInitials(name?: string | null, fallbackEmail?: string) {
  if (name && name.trim()) {
    return name
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('')
      .slice(0, 2) || (fallbackEmail ? fallbackEmail.slice(0, 2).toUpperCase() : '??');
  }
  if (fallbackEmail) {
    return fallbackEmail.slice(0, 2).toUpperCase();
  }
  return '??';
}

function formatDigestDate(date: Date) {
  const now = new Date();
  const messageDate = new Date(date);
  const diffInHours = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60);

  if (diffInHours < 24) {
    return messageDate.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }
  if (diffInHours < 24 * 7) {
    return messageDate.toLocaleDateString(undefined, { weekday: 'short' });
  }
  return messageDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function buildEmailBodyHtml(email: Email): SanitizedEmail | null {
  // Only show emails with full HTML body - no fallbacks
  if (!email.bodyHtml || email.bodyHtml.trim().length === 0) {
    return null;
  }

  const cidMap: Record<string, string> = {};
  email.attachments?.forEach((attachment) => {
    if (attachment.validated === false || !attachment.contentId) {
      return;
    }
    const cid = attachment.contentId.replace(/^<|>$/g, '');
    const source = attachment.previewUrl ?? attachment.downloadUrl ?? null;
    if (cid && source) {
      cidMap[cid] = source;
    }
  });

  const sanitized = sanitizeEmailHtml(email.bodyHtml);
  return { ...sanitized, body: applyCidMap(sanitized.body, cidMap) };
}

function getEmailBodySignature(email: Email): string {
  const base =
    email.bodyHtml?.trim() ??
    email.body?.trim() ??
    email.snippet?.trim() ??
    '';
  const attachmentSignature = (email.attachments ?? [])
    .map((attachment) => [
      attachment.id,
      attachment.contentId,
      attachment.previewUrl,
      attachment.downloadUrl,
      attachment.validated ? '1' : '0',
    ].join(':'))
    .join('|');
  return `${email.id}:${base.length}:${base.slice(0, 64)}:${attachmentSignature}`;
}

function applyCidMap(html: string, cidMap: Record<string, string>): string {
  let output = html;
  Object.entries(cidMap).forEach(([cid, source]) => {
    const pattern = new RegExp(`cid:${cid}`, 'gi');
    output = output.replace(pattern, source);
  });
  return output;
}

function DigestMessageCard({
  email,
  onVisible,
  onHydrate,
  onActiveChange,
  className,
  children,
}: {
  email: Email;
  onVisible?: (email: Email) => void;
  onHydrate?: (email: Email) => void;
  onActiveChange?: (email: Email, info: { isVisible: boolean; ratio: number }) => void;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const hasReportedRef = useRef<boolean>(email.read);

  useEffect(() => {
    const target = ref.current;
    const supportsObserver = typeof IntersectionObserver !== 'undefined' && target;
    if (!supportsObserver) {
      onHydrate?.(email);
      onActiveChange?.(email, { isVisible: true, ratio: 1 });
      if (!email.read && onVisible && !hasReportedRef.current) {
        hasReportedRef.current = true;
        onVisible(email);
      }
      return () => {
        onActiveChange?.(email, { isVisible: false, ratio: 0 });
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        onActiveChange?.(email, { isVisible: entry.isIntersecting, ratio: entry.intersectionRatio ?? 0 });
        if (entry.isIntersecting) {
          onHydrate?.(email);
          if (!email.read && onVisible && !hasReportedRef.current) {
            hasReportedRef.current = true;
            onVisible(email);
          }
        }
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    observer.observe(target);
    return () => {
      observer.disconnect();
      onActiveChange?.(email, { isVisible: false, ratio: 0 });
    };
  }, [email, onActiveChange, onHydrate, onVisible]);

  useEffect(() => {
    if (email.read) {
      hasReportedRef.current = true;
    }
  }, [email]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
